import React from "react";
import { deliveryLabel, settlementLabel } from "@/lib/orderLabels";

/**
 * Unified chronological feed combining:
 *   - order.history (delivery state transitions)
 *   - order.settlement.history (settlement transitions)
 *   - order.fulfillment_history (sub-milestone transitions)
 * Role-aware wording.
 */
export default function OrderAuditFeed({ order, role }) {
  const events = [];

  for (const h of (order.history || [])) {
    events.push({
      at: h.at, kind: "delivery", tone: "amber",
      label: deliveryLabel(h.status, role) || h.status,
      notes: h.notes,
    });
  }
  for (const h of (order.settlement?.history || [])) {
    events.push({
      at: h.at, kind: "settlement", tone: "blue",
      label: settlementLabel(h.status, role) || h.status,
      notes: h.notes || h.reason || h.method,
    });
  }
  for (const h of (order.fulfillment_history || [])) {
    const pretty = String(h.milestone).replace(/_/g, " ");
    events.push({
      at: h.at, kind: "fulfillment", tone: "muted",
      label: prettyMilestone(h.milestone, role),
      notes: [h.notes, h.carrier, h.eta].filter(Boolean).join(" · "),
    });
  }

  events.sort((a, b) => (a.at < b.at ? 1 : -1));

  if (!events.length) {
    return <div className="empty-state">
      <div className="es-title">No activity yet</div>
      <div className="es-body">Status changes, invoice events, and fulfillment milestones will appear here.</div>
    </div>;
  }

  return (
    <div className="order-audit" data-testid="order-audit-feed">
      <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 12 }}>
        AUDIT TIMELINE
      </div>
      <ol className="audit-list">
        {events.map((e, i) => (
          <li key={i} className={"audit-item audit-" + e.tone} data-testid={`audit-item-${i}`}>
            <span className="audit-dot" aria-hidden />
            <div className="audit-meta">
              <div className="audit-label">{e.label}</div>
              <div className="audit-time font-mono" title={new Date(e.at).toISOString()}>{relTime(e.at)}</div>
              {e.notes && <div className="audit-notes">{e.notes}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function prettyMilestone(m, role) {
  const buyer = {
    awaiting_assembly: "Awaiting assembly",
    in_assembly: "Order in assembly",
    ready_to_ship: "Ready to ship",
    picked_up: "Picked up by carrier",
    out_for_delivery: "Out for delivery",
  };
  const supplier = {
    awaiting_assembly: "Awaiting assembly",
    in_assembly: "Assembly started",
    ready_to_ship: "Marked ready to ship",
    picked_up: "Handed to carrier",
    out_for_delivery: "Out for delivery",
  };
  return (role === "supplier" ? supplier : buyer)[m] || m.replace(/_/g, " ");
}

function relTime(iso) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    const days = Math.round(h / 24);
    if (days < 7) return `${days} d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}
